import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import api from '../../api/axios';

// "History" on a published shift: who held it since publishing, e.g.
// "Anna – published 28 Oct · Ben – cover, 3 Nov 14:02". Labels come from the
// server (neutral for staff; admins also get the exact type and who changed it).
// Drafts show nothing. More than 3 entries: the latest 3, the rest on request.

const VISIBLE = 3;

// One entry as text: { who, what }
export function historyLine(e) {
  const who = e.open ? 'Open' : (e.user_name || '—');
  const at = new Date(e.at);
  let what;
  if (e.kind === 'published') what = `published ${format(at, 'd MMM')}`;
  else if (e.kind === 'as_of') what = `as of ${format(at, 'd MMM')}`;
  else if (e.open && e.label === 'Open') what = format(at, 'd MMM HH:mm');
  else what = `${e.label.charAt(0).toLowerCase()}${e.label.slice(1)}, ${format(at, 'd MMM HH:mm')}`;
  if (e.by) what += ` · by ${e.by}`;
  return { who, what };
}

export default function ShiftHistory({ slot, boxed = false }) {
  const [entries, setEntries] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const published = slot?.period_status === 'published';

  useEffect(() => {
    setEntries(null);
    setShowAll(false);
    if (!slot?.id || !published) return undefined;
    let alive = true;
    api.get(`/shifts/slots/${slot.id}/history`)
      .then((r) => { if (alive) setEntries(r.data.entries || []); })
      .catch(() => { if (alive) setEntries([]); });
    return () => { alive = false; };
  }, [slot?.id, published, slot?.assigned_user_id]);

  if (!published || !entries || entries.length === 0) return null;
  const hidden = showAll ? 0 : Math.max(0, entries.length - VISIBLE);
  const shown = entries.slice(hidden);

  return (
    <div
      data-testid="shift-history"
      style={boxed
        ? { marginBottom: '14px', padding: '8px 12px', borderRadius: 'var(--radius)', background: 'var(--bg)', border: '1px solid var(--border)' }
        : { marginTop: '4px' }}
    >
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 4px' }}>History</p>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          style={{ background: 'none', border: 'none', padding: '4px 0', minHeight: '32px', color: 'var(--primary-dark)', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}
        >
          Show {hidden} earlier
        </button>
      )}
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {shown.map((e, i) => {
          const { who, what } = historyLine(e);
          const current = hidden + i === entries.length - 1;
          return (
            <li key={`${e.at}-${hidden + i}`} style={{ fontSize: '0.84rem', lineHeight: 1.4, overflowWrap: 'anywhere', color: current ? 'var(--text)' : 'var(--text-light)' }}>
              <strong style={{ fontWeight: current ? 700 : 600, color: e.open ? '#B91C1C' : undefined }}>{who}</strong>
              {' – '}{what}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
